import { useMemo, useState } from 'react';
import clsx from 'clsx';
import Lightbox, {
  SlotStyles,
  ViewCallbackProps,
} from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/plugins/video';

import colors from '@atom/styles/colors';
import { Media } from '@atom/types/media';

import MediaLightboxDetails from './MediaLightboxDetails';
import MediaLightboxNavButton from './MediaLightboxNavButton';
import MediaLightboxToolbar from './MediaLightboxToolbar';
import { toSlides } from './mediaSlides';

import 'yet-another-react-lightbox/styles.css';
import classes from './mediaLightbox.module.css';

const DETAILS_OFFSET = 'var(--detailsShown) * var(--detailsWidth)';
const LIGHTBOX_Z_INDEX = 1100;

const LIGHTBOX_STYLES: SlotStyles = {
  root: { zIndex: LIGHTBOX_Z_INDEX },
  container: { backgroundColor: colors.opacity.black0_9 },
  toolbar: {
    left: 0,
    right: `calc(${DETAILS_OFFSET})`,
    padding: 0,
    transition: 'right 0.2s ease',
  },
  slide: {
    padding: `calc(var(--toolbarHeight) + 1rem) calc(3.5rem + ${DETAILS_OFFSET}) 1rem 3.5rem`,
  },
};

export type MediaLightboxProps = {
  open: boolean;
  media: Media[];
  selectedMediaId: string;
  canUpdateMedia: boolean;
  canDeleteMedia: boolean;
  canMarkAsCoverPhoto?: boolean;
  coverPhotoFileId?: string;
  onClose: () => void;
  onSelectedMediaChange?: (mediaId: string) => void;
  onRename?: (mediaId: string, name: string) => void;
  onDelete?: (mediaId: string) => void;
  onCoverPhotoToggle?: (mediaId: string) => void;
  onRefetch?: () => void;
  className?: string;
};

const MediaLightbox = ({
  open,
  media,
  selectedMediaId,
  canUpdateMedia,
  canDeleteMedia,
  canMarkAsCoverPhoto = false,
  coverPhotoFileId,
  onClose,
  onSelectedMediaChange = () => {},
  onRename = () => {},
  onDelete = () => {},
  onCoverPhotoToggle = () => {},
  onRefetch,
  className,
}: MediaLightboxProps) => {
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);

  const slides = useMemo(() => toSlides(media), [media]);

  const selectedIndex = Math.max(
    media.findIndex(medium => medium.id === selectedMediaId),
    0,
  );
  const selectedMedia = media[selectedIndex];

  const handleClose = () => {
    setDetailsOpen(false);
    onClose();
  };

  const handleView = ({ index }: ViewCallbackProps) => {
    const viewed = media[index];

    if (viewed) {
      onSelectedMediaChange(viewed.id);
    }
  };

  if (!selectedMedia) {
    return null;
  }

  const renderNavButton = (direction: 'prev' | 'next') => () => (
    <MediaLightboxNavButton direction={direction} />
  );

  const hasMultipleSlides = media.length > 1;

  return (
    <Lightbox
      open={open}
      close={handleClose}
      index={selectedIndex}
      slides={slides}
      plugins={[Video]}
      carousel={{
        finite: false,
        padding: 0,
        spacing: 0,
      }}
      styles={LIGHTBOX_STYLES}
      className={clsx(
        classes.lightbox,
        detailsOpen && classes.detailsShown,
        className,
      )}
      on={{ view: handleView }}
      toolbar={{
        buttons: [
          <MediaLightboxToolbar
            key="mediaLightboxToolbar"
            media={selectedMedia}
            isCoverPhoto={
              !!coverPhotoFileId && coverPhotoFileId === selectedMedia.fileId
            }
            canUpdateMedia={canUpdateMedia}
            canDeleteMedia={canDeleteMedia}
            canMarkAsCoverPhoto={canMarkAsCoverPhoto}
            onClose={handleClose}
            onRename={onRename}
            onDelete={onDelete}
            onCoverPhotoToggle={onCoverPhotoToggle}
            onDetailsToggle={() => setDetailsOpen(current => !current)}
          />,
        ],
      }}
      render={{
        buttonPrev: hasMultipleSlides ? renderNavButton('prev') : () => null,
        buttonNext: hasMultipleSlides ? renderNavButton('next') : () => null,
        controls: () => (
          <MediaLightboxDetails
            media={selectedMedia}
            canUpdateMedia={canUpdateMedia}
            onClose={() => setDetailsOpen(false)}
            onRefetch={onRefetch}
          />
        ),
      }}
    />
  );
};

export default MediaLightbox;
